import { tC } from "../../utils/TranslateWithContentLanguage";
import Markdown from "react-markdown";
import CardImage from "../tool_components/CardImage";
import { Button } from "@core/index";
import { RefreshCw, Maximize, Minimize } from "lucide-react";
import PdfDownloadButton from "../pdf/PdfDownloadButton";
import useImagePdf from "../pdf/useImagePdf";
import AiAssessmentScaleCardPdf from "./AiAssessmentScaleCardPdf";
import { useState } from "react";
import { MascotBubble } from "../tool_components/MascotBubble";
import { useAppSelector } from "@packages/store/hooks";
import PdfCustomizationModal, {
  PdfCustomizationSettings,
} from "@features/common/components/pdf-customization-modal";
import {
  getGetUserAiAssessmentScalesQueryKey,
  getGetAiAssessmentScaleQueryKey,
} from "@packages/api/madlenAPI";
import useIsAuthor from "@packages/hooks/useIsAuthor";
import RegeneratingOverlay from "../tool_components/RegenLoadingOverlay";

// Helper to get level name translation key based on level number
const getLevelNameKey = (levelNumber: number) => {
  switch (levelNumber) {
    case 1:
      return "noAi"; // NO AI
    case 2:
      return "aiPlanning"; // AI PLANNING
    case 3:
      return "aiCollaboration"; // AI COLLABORATION
    case 4:
      return "fullAi"; // FULL AI
    case 5:
      return "aiExploration"; // AI EXPLORATION
    default:
      return "";
  }
};

export interface AiAssessmentScaleCardProps {
  assessmentScale: {
    id: number;
    imageUrl?: string;
    tableTitle?: string;
    title?: string;
    courseName?: string;
    gradeLevel?: number;
    description?: string;
    context?: string;
    levels?: Array<{
      level_number?: number;
      level_name?: string;
      description?: string;
      student_guidance?: string;
      examples?: string[];
    }>;
    authorName: string;
    authorId: number;
    likeCount: number;
    didUserLiked?: boolean;
    createdAt: string;
    isPublic: boolean;
    approvalStatus: string;
    status?: string;
    contentLanguage: string;
  };
  isImageLoading?: boolean;
  onCreateAgain?: () => void;
  customizationSettings?: PdfCustomizationSettings;
}

export const AiAssessmentScaleCard = ({
  assessmentScale,
  isImageLoading,
  onCreateAgain,
}: Omit<AiAssessmentScaleCardProps, "customizationSettings">) => {
  const finalImageUrl = useImagePdf(assessmentScale.imageUrl);
  const contentLanguage = assessmentScale.contentLanguage || "tr";
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [showPdfCustomizationModal, setShowPdfCustomizationModal] =
    useState(false);
  const customizationSettings = useAppSelector(
    (state) => state.user.teacher?.preferences?.pdfOptions,
  );
  // Sort levels by level_number if levels exist
  const sortedLevels = assessmentScale.levels
    ? [...assessmentScale.levels].sort(
        (a, b) => (a.level_number || 0) - (b.level_number || 0),
      )
    : [];

  // Toggle full screen mode
  const toggleFullScreen = () => {
    setIsFullScreen(!isFullScreen);
  };
  const isAuthor = useIsAuthor(assessmentScale.authorId);
  return (
    <div className="w-full px-1">
      {isAuthor && (
        <MascotBubble
          toolsQueryKey={getGetUserAiAssessmentScalesQueryKey()}
          toolQueryKey={getGetAiAssessmentScaleQueryKey(assessmentScale.id)}
          createChatParams={{
            teacherToolId: assessmentScale.id,
            toolType: "AI_ASSESSMENT_SCALE",
          }}
        />
      )}
      {/* Header Section */}
      {!isFullScreen && (
        <>
          <CardImage
            imageUrl={assessmentScale.imageUrl}
            courseName={assessmentScale.courseName || ""}
            gradeLevel={assessmentScale.gradeLevel || 0}
            theme="teal"
            isLoading={isImageLoading}
            createdAt={assessmentScale.createdAt}
            topic={assessmentScale.title || assessmentScale.tableTitle || ""}
            badges={null}
            contentLanguage={contentLanguage}
          />

          <div className="flex justify-end items-center gap-2 my-3">
            {onCreateAgain && (
              <Button
                onClick={onCreateAgain}
                variant="solid"
                size="md"
                textColor="text-white dark:text-white"
                bgColor="bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700 dark:hover:from-teal-700 dark:hover:to-teal-800"
                rounded="xl"
              >
                <RefreshCw size={18} />
                <span>{tC(contentLanguage, "common.createAnother")}</span>
              </Button>
            )}
            <PdfDownloadButton
              openCustomizationModal={() => setShowPdfCustomizationModal(true)}
              pdfTitle={
                assessmentScale.title || assessmentScale.tableTitle || ""
              }
              document={
                <AiAssessmentScaleCardPdf
                  customizationSettings={customizationSettings}
                  assessmentScale={{
                    ...assessmentScale,
                    imageUrl: finalImageUrl,
                  }}
                  onCreateAgain={onCreateAgain}
                />
              }
              btnTheme="bg-gradient-to-r from-teal-500 to-teal-600"
            />
          </div>

          {/* Description Section - if available - Full Width */}
          {assessmentScale.description && (
            <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border border-gray-200 dark:border-gray-700 mb-3 mx-auto max-w-4xl">
              <h4 className="text-xl font-semibold text-teal-700 dark:text-teal-300 mb-2 text-center">
                {tC(contentLanguage, "aiAssessmentScale.descriptionTable")}
              </h4>
              <div className="prose prose-md dark:prose-invert max-w-none text-center">
                <Markdown>{assessmentScale.description}</Markdown>
              </div>
            </div>
          )}

          {/* Context Section - if available */}
          {assessmentScale.context && (
            <div className="grid grid-cols-1 gap-2 mb-2">
              <div className="bg-white dark:bg-gray-800 rounded-xl p-2 shadow-sm border border-gray-200 dark:border-gray-700">
                <h4 className="text-base font-semibold text-teal-700 dark:text-teal-300 mb-1">
                  {tC(contentLanguage, "aiAssessmentScale.context")}
                </h4>
                <div className="prose prose-sm dark:prose-invert max-w-none">
                  <Markdown>{assessmentScale.context}</Markdown>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* Table View */}
      {sortedLevels.length > 0 && (
        <div
          className={`bg-white dark:bg-gray-800 h-full rounded-xl shadow-lg border border-gray-400 dark:border-gray-700 mb-4 transition-all duration-500 ${
            isFullScreen ? "fixed inset-0 z-50 p-2 flex flex-col" : ""
          }`}
          style={{
            backgroundColor: isFullScreen ? "#f0f9ff" : undefined,
          }}
        >
          {/* Title Banner with Full Screen Button */}
          <div
            className={`bg-gradient-to-r from-teal-200 to-cyan-200 dark:from-teal-700 dark:to-cyan-700 text-center py-1 relative rounded-t-xl flex justify-between items-center px-4 ${
              isFullScreen ? "flex-shrink-0" : ""
            }`}
          >
            <div className="w-10"></div> {/* Placeholder for balance */}
            <h2 className="font-bold text-lg text-gray-800 dark:text-gray-100 mx-auto">
              {assessmentScale.tableTitle ||
                assessmentScale.title ||
                tC(contentLanguage, "aiAssessmentScale.title")}
            </h2>
            {/* Full Screen Toggle Button */}
            <Button
              onClick={toggleFullScreen}
              variant="outline"
              size="sm"
              className="bg-white dark:bg-gray-700 shadow-sm hover:shadow-md border-gray-300 dark:border-gray-500"
              rounded="full"
            >
              {isFullScreen ? (
                <Minimize
                  size={18}
                  className="text-teal-700 dark:text-teal-300"
                />
              ) : (
                <Maximize
                  size={18}
                  className="text-teal-700 dark:text-teal-300"
                />
              )}
            </Button>
          </div>

          <div
            className={`w-full relative ${isFullScreen ? "flex-grow flex h-full overflow-y-auto" : ""}`}
          >
            {isFullScreen ? (
              // Full-screen optimized view - using table layout for consistency
              <table className="w-full h-full table-fixed border-collapse border border-gray-400 dark:border-gray-600 overflow-y-auto">
                {/* Table Header */}
                <thead>
                  <tr className="bg-gradient-to-r from-teal-200 to-cyan-200 dark:from-teal-700 dark:to-cyan-700 border border-gray-400 dark:border-gray-600">
                    <th className="py-1 px-1 border border-gray-400 dark:border-gray-600 font-bold text-gray-800 dark:text-gray-100 w-[5%] text-center text-xs">
                      {tC(contentLanguage, "aiAssessmentScale.level")}
                    </th>
                    <th className="py-1 px-1 border border-gray-400 dark:border-gray-600 font-bold text-gray-800 dark:text-gray-100 w-[15%] text-center text-xs">
                      {tC(contentLanguage, "aiAssessmentScale.levelName")}
                    </th>
                    <th className="py-1 px-1 border border-gray-400 dark:border-gray-600 font-bold text-gray-800 dark:text-gray-100 w-[40%] text-center text-xs">
                      {tC(
                        contentLanguage,
                        "aiAssessmentScale.descriptionTable",
                      )}
                    </th>
                    <th className="py-1 px-1 border border-gray-400 dark:border-gray-600 font-bold text-gray-800 dark:text-gray-100 w-[40%] text-center text-xs">
                      {tC(contentLanguage, "aiAssessmentScale.examples")}
                    </th>
                  </tr>
                </thead>

                {/* Table Body */}
                <tbody className="divide-y-2 divide-gray-400">
                  {sortedLevels.map((level) => {
                    const levelNumber = level.level_number || 0;

                    // Color schemes based on the image
                    let rowBgColor = "";
                    switch (levelNumber) {
                      case 1:
                        rowBgColor = "bg-cyan-100 dark:bg-cyan-900/30";
                        break;
                      case 2:
                        rowBgColor = "bg-emerald-100 dark:bg-emerald-900/30";
                        break;
                      case 3:
                        rowBgColor = "bg-indigo-100 dark:bg-indigo-900/30";
                        break;
                      case 4:
                        rowBgColor = "bg-orange-100 dark:bg-orange-900/30";
                        break;
                      case 5:
                        rowBgColor = "bg-fuchsia-100 dark:bg-fuchsia-900/30";
                        break;
                      default:
                        rowBgColor = "bg-gray-100 dark:bg-gray-800/50";
                    }

                    return (
                      <tr
                        key={levelNumber}
                        className={`${rowBgColor} transition-colors duration-300 hover:brightness-105 dark:hover:brightness-125 h-1/5`}
                      >
                        {/* Level Number */}
                        <td className="p-0.5 border border-gray-400 dark:border-gray-600 text-center font-bold align-middle">
                          <div className="w-6 h-6 rounded-full flex items-center justify-center mx-auto text-xs font-bold text-white bg-gradient-to-r from-teal-500 to-teal-600 dark:from-teal-500 dark:to-teal-600 shadow-sm">
                            {levelNumber}
                          </div>
                        </td>

                        {/* Level Name */}
                        <td className="py-0 px-1 border border-gray-400 dark:border-gray-600 text-center align-middle">
                          <div className="text-xs font-bold break-words hyphens-auto text-gray-800 dark:text-gray-200 flex items-center justify-center h-full px-0.5 leading-tight">
                            {levelNumber > 0
                              ? tC(
                                  contentLanguage,
                                  `aiAssessmentScale.levelNames.${getLevelNameKey(levelNumber)}`,
                                )
                              : level.level_name || ""}
                          </div>
                        </td>

                        {/* Description */}
                        <td className="py-0.5 px-1 border border-gray-400 dark:border-gray-600 align-middle overflow-hidden">
                          <div className="text-xs leading-tight overflow-auto text-gray-800 dark:text-gray-200 max-h-full pr-1 flex flex-col justify-center h-full">
                            <div className="space-y-1">
                              {level.description?.split("\n").map((line, i) => (
                                <div key={i} className="text-left">
                                  <Markdown
                                    components={{
                                      p: ({ children }) => (
                                        <p className="my-1.5">{children}</p>
                                      ),
                                      strong: ({ children }) => (
                                        <strong className="font-bold text-teal-700 dark:text-teal-300">
                                          {children}
                                        </strong>
                                      ),
                                    }}
                                  >
                                    {line}
                                  </Markdown>
                                </div>
                              ))}
                            </div>
                          </div>
                        </td>

                        {/* Examples/Guidance - FULLSCREEN MODE */}
                        <td className="py-0.5 px-1 border border-gray-400 dark:border-gray-600 align-middle overflow-hidden">
                          <div className="text-xs overflow-auto max-h-full pr-1 flex flex-col justify-center h-full">
                            {level.student_guidance && (
                              <div className="mb-1 font-medium text-gray-800 dark:text-gray-200 p-1 rounded border-l-4 border-teal-500 pl-2 bg-teal-50/50 dark:bg-teal-900/20">
                                {level.student_guidance}
                              </div>
                            )}

                            {level.examples && level.examples.length > 0 && (
                              <ul className="list-disc marker:text-teal-600 dark:marker:text-teal-400 pl-4 text-xs space-y-1 text-gray-800 dark:text-gray-200">
                                {level.examples.map((example, idx) => (
                                  <li
                                    key={idx}
                                    className="mb-0.5 leading-tight"
                                  >
                                    <Markdown
                                      components={{
                                        p: ({ children }) => <>{children}</>,
                                        strong: ({ children }) => (
                                          <strong className="font-bold text-teal-700 dark:text-teal-300">
                                            {children}
                                          </strong>
                                        ),
                                      }}
                                    >
                                      {example}
                                    </Markdown>
                                  </li>
                                ))}
                              </ul>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            ) : (
              // Regular table view for non-fullscreen
              <table className="w-full table-fixed border-collapse border border-gray-400 dark:border-gray-600">
                {/* Table Header */}
                <thead>
                  <tr className="bg-gradient-to-r from-teal-200 to-cyan-200 dark:from-teal-700 dark:to-cyan-700 border border-gray-400 dark:border-gray-600">
                    <th className="py-2 px-1 border border-gray-400 dark:border-gray-600 font-bold text-gray-700 dark:text-gray-100 w-[5%] text-center">
                      {tC(contentLanguage, "aiAssessmentScale.level")}
                    </th>
                    <th className="py-2 px-1 border border-gray-400 dark:border-gray-600 font-bold text-gray-700 dark:text-gray-100 w-[15%] text-center">
                      {tC(contentLanguage, "aiAssessmentScale.levelName")}
                    </th>
                    <th className="py-2 px-1 border border-gray-400 dark:border-gray-600 font-bold text-gray-700 dark:text-gray-100 w-[40%] text-center">
                      {tC(
                        contentLanguage,
                        "aiAssessmentScale.descriptionTable",
                      )}
                    </th>
                    <th className="py-2 px-1 border border-gray-400 dark:border-gray-600 font-bold text-gray-700 dark:text-gray-100 w-[40%] text-center">
                      {tC(contentLanguage, "aiAssessmentScale.examples")}
                    </th>
                  </tr>
                </thead>

                {/* Table Body */}
                <tbody>
                  {sortedLevels.map((level) => {
                    const levelNumber = level.level_number || 0;

                    // Color schemes based on the image
                    let rowBgColor = "";
                    switch (levelNumber) {
                      case 1:
                        rowBgColor = "bg-cyan-100 dark:bg-cyan-900/30";
                        break;
                      case 2:
                        rowBgColor = "bg-emerald-100 dark:bg-emerald-900/30";
                        break;
                      case 3:
                        rowBgColor = "bg-indigo-100 dark:bg-indigo-900/30";
                        break;
                      case 4:
                        rowBgColor = "bg-orange-100 dark:bg-orange-900/30";
                        break;
                      case 5:
                        rowBgColor = "bg-fuchsia-100 dark:bg-fuchsia-900/30";
                        break;
                      default:
                        rowBgColor = "bg-gray-100 dark:bg-gray-800/50";
                    }

                    return (
                      <tr
                        key={levelNumber}
                        className={`${rowBgColor} transition-colors duration-300 hover:brightness-105 dark:hover:brightness-125 h-1/5`}
                      >
                        {/* Level Number */}
                        <td className="p-0.5 border border-gray-400 dark:border-gray-600 text-center font-bold align-middle">
                          <div className="w-6 h-6 rounded-full flex items-center justify-center mx-auto text-xs font-bold text-white bg-gradient-to-r from-teal-500 to-teal-600 dark:from-teal-500 dark:to-teal-600 shadow-sm">
                            {levelNumber}
                          </div>
                        </td>

                        {/* Level Name */}
                        <td className="py-2 px-1 border border-gray-400 dark:border-gray-600 text-center align-middle">
                          <div className="text-sm font-bold break-words hyphens-auto text-gray-800 dark:text-gray-100 px-0.5 leading-tight">
                            {levelNumber > 0
                              ? tC(
                                  contentLanguage,
                                  `aiAssessmentScale.levelNames.${getLevelNameKey(levelNumber)}`,
                                )
                              : level.level_name || ""}
                          </div>
                        </td>

                        {/* Description */}
                        <td className="py-4 px-5 border border-gray-400 dark:border-gray-600 align-middle">
                          <div className="text-sm leading-tight text-gray-800 dark:text-gray-100 flex flex-col justify-center h-full">
                            <div className="space-y-2">
                              {level.description?.split("\n").map((line, i) => (
                                <div key={i} className="text-left">
                                  <Markdown
                                    components={{
                                      p: ({ children }) => (
                                        <p className="my-2">{children}</p>
                                      ),
                                      strong: ({ children }) => (
                                        <strong className="font-bold text-teal-700 dark:text-teal-300">
                                          {children}
                                        </strong>
                                      ),
                                    }}
                                  >
                                    {line}
                                  </Markdown>
                                </div>
                              ))}
                            </div>
                          </div>
                        </td>

                        {/* Examples/Guidance - REGULAR MODE */}
                        <td className="py-4 px-4 border border-gray-400 dark:border-gray-600 align-middle">
                          <div className="flex flex-col justify-center h-full">
                            {level.student_guidance && (
                              <div className="text-sm mb-2 font-medium px-3 py-2 rounded-md shadow-sm border-l-4 border-teal-500 bg-teal-50/50 dark:bg-teal-900/20 text-gray-800 dark:text-gray-100">
                                {level.student_guidance}
                              </div>
                            )}

                            {level.examples && level.examples.length > 0 && (
                              <ul className="list-disc marker:text-teal-600 dark:marker:text-teal-400 pl-5 text-sm space-y-1.5 rounded-md text-gray-800 dark:text-gray-100">
                                {level.examples.map((example, idx) => (
                                  <li
                                    key={idx}
                                    className="mb-1.5 leading-relaxed"
                                  >
                                    <Markdown
                                      components={{
                                        p: ({ children }) => <>{children}</>,
                                        strong: ({ children }) => (
                                          <strong className="font-bold text-teal-700 dark:text-teal-300">
                                            {children}
                                          </strong>
                                        ),
                                      }}
                                    >
                                      {example}
                                    </Markdown>
                                  </li>
                                ))}
                              </ul>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
            {assessmentScale.status === "REGENERATING" && (
              <RegeneratingOverlay />
            )}
          </div>
        </div>
      )}

      {/* Attribution Footer */}
      {!isFullScreen && (
        <div className="text-center mb-6">
          <a
            href="https://leonfurze.com/2024/08/28/updating-the-ai-assessment-scale/"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:no-underline"
          >
            <p className="text-xl font-bold text-gray-800 dark:text-gray-200 mb-1 hover:text-teal-700 dark:hover:text-teal-300">
              AI Assessment Scale (AIAS) 2024 by Perkins, Furze, Roe & MacVaugh
            </p>
          </a>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            <a
              href="https://leonfurze.com/2024/08/28/updating-the-ai-assessment-scale/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-teal-600 hover:underline dark:text-teal-400"
            ></a>
          </p>
        </div>
      )}
      {/* PDF Customization Modal */}
      <PdfCustomizationModal
        show={showPdfCustomizationModal}
        onClose={() => setShowPdfCustomizationModal(false)}
        hideSchoolLogoOptions
        documentComponent={
          <AiAssessmentScaleCardPdf
            customizationSettings={customizationSettings}
            assessmentScale={{
              ...assessmentScale,
              imageUrl: finalImageUrl,
            }}
            onCreateAgain={onCreateAgain}
          />
        }
      />
    </div>
  );
};

export default AiAssessmentScaleCard;
